import {
  Box,
  Flex,
  Skeleton,
  Text,
  Tooltip,
  VStack,
  useMediaQuery,
  useOutsideClick,
} from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import AddButton from 'components/AddButton';
import HelpComponent from 'components/CustomHelp';
import ArrowLeftBlocks from 'components/Icons/ArrowLeftBlocks';
import Cross from 'components/Icons/Cross';
import transformDimensions from 'connectors/transformDimensions';
import ErrorPage from 'pages/ErrorPage';
import { STATUS } from 'projectConstants';
import { getExandSideInputStatus, handleExpandSideInput } from 'redux/BuilderModeSlice';
import {
  addDimensionItem,
  fetchDimensionItems,
  fetchIndicatorDetail,
  fetchIndicatorDetailType,
  fetchModelTime,
  geModelTimeStatus,
  getChartFilterItems,
  getDimensionItems,
  getIndicatorDetail,
  getIndicatorDetailStatus,
  getModelTime,
  getTableShimmer,
  resetIndicator,
} from 'redux/IndicatorPageSlice';
import { getCurrentDimensionIndex } from 'redux/InputPageSlice';
import {
  FetchPlangPageIndicatorTableData,
  fetchBlockDrivers,
  onErrorShow,
  planPageShow,
  getmultiDimFiltersItems,
  getIndicatorTableData,
  setDriver,
} from 'redux/PlanPageSlice';
import {
  FetchDataInput,
  getBaseScenario,
  getExpandValue,
  getSelectedIndicators,
  handleToggleInputPanel,
} from 'redux/PlannerModeSlice';
import addNewDimensionItem from 'utils/AddNewDimItem';
import QuestionMarkIcon from 'components/Icons/QuestionMarkIcon';
import { AppDispatch } from 'utils/GlobalHelpers';
import { getToolbarIndicatorType } from 'utils/resolveSelectedIndicatorFromMetadata';
import { neutral, radius, text as textToken } from 'styles/revampTokens';

import Chart from './Chart';
import IndicatorTable from './IndicatorTable';
import InputTypeGroupedButton from './InputTypeGroupedButton';
import EmptyData from './EmptyData';

export interface Properties {
  refetchRequest: () => void;
}

const IndicatorPageOPZ = () => {
  const dispatch: AppDispatch = useDispatch();
  const currentDriver = useSelector(getSelectedIndicators);
  const containerReference = useRef<HTMLDivElement>(null);
  const [innerHeight, setInnerHeight] = useState(0);
  const indicatorTableData = useSelector(getIndicatorTableData);
  const indicator = useSelector(getIndicatorDetail);
  const isExpand = useSelector(getExpandValue);
  const isSideInputExpand = useSelector(getExandSideInputStatus);
  const filteredChartItems = useSelector(getChartFilterItems);
  const multiDimFiltersItems = useSelector(getmultiDimFiltersItems);
  useLayoutEffect(() => {
    if (containerReference.current) {
      const containerHeight = containerReference.current.offsetHeight;
      const containerPadding =
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingLeft, 10) +
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingRight, 10);
      const heightWithoutPadding = containerHeight - containerPadding;
      setInnerHeight(heightWithoutPadding);
    }
  }, [containerReference?.current]);

  const dimensionItems = useSelector(getDimensionItems);
  const { pathname } = useLocation();
  const { modelId, blockId } = useParams();

  const indicatorDetailStatus = useSelector(getIndicatorDetailStatus);
  const tabShimmer = useSelector(getTableShimmer);
  const modelTimeStatus = useSelector(geModelTimeStatus);

  const [isMobile] = useMediaQuery('(max-width: 1024px)');
  const [isSmallScreen] = useMediaQuery('(max-height: 820px)');
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const modelTime = useSelector(getModelTime);

  const currentDimensionIndex = useSelector(getCurrentDimensionIndex);
  const showPlanPage = useSelector(planPageShow);
  const baseScenario = useSelector(getBaseScenario);

  const chartFilterDimId = indicatorTableData?.column_headers?.[0]?.is_dimension
    ? indicatorTableData.column_headers[0].id
    : null;

  const tooltipReference = useRef<any>(null);
  const dimensionId = currentDriver?.data_input?.dimensions?.[currentDimensionIndex];

  useOutsideClick({
    ref: tooltipReference,

    handler: () => {
      setIsTooltipOpen(false);
    },
  });

  const fetchModelTimeData = () => {
    dispatch(fetchModelTime(Number(modelId)));
  };

  const fetchDimItemsData = () => {
    dispatch(
      fetchDimensionItems({
        dimensionId,
        parameters: {
          scenario_id: baseScenario?.id,
        },
      }),
    );
  };

  const previousDriverIdReference = useRef<number | null>(null);

  useEffect(() => {
    if (!currentDriver?.id) return;

    if (
      previousDriverIdReference.current != null &&
      previousDriverIdReference.current !== currentDriver.id
    ) {
      dispatch(resetIndicator());
    }
    previousDriverIdReference.current = currentDriver.id;
    dispatch(setDriver(currentDriver));
  }, [currentDriver?.id, dispatch]);

  useEffect(() => {
    if (modelId && blockId) {
      fetchModelTimeData();
    }
  }, [pathname, currentDriver]);

  useEffect(() => {
    if (currentDriver && currentDriver?.data_input?.dimensions?.length > 0) {
      fetchDimItemsData();
    }
  }, [pathname, currentDriver?.id, baseScenario?.id, dimensionId]);

  const isInputDriver = getToolbarIndicatorType(currentDriver) === 'input';

  useEffect(() => {
    if (modelId && blockId && isInputDriver && baseScenario?.id && currentDriver?.id) {
      dispatch(
        FetchPlangPageIndicatorTableData({
          indicator_id: currentDriver.id,
          parameters: {
            scenario_id: baseScenario.id,
            format_excel_data: true,
          },
        }),
      );
    }
  }, [currentDriver?.id, baseScenario?.id, blockId, modelId, isInputDriver, dispatch]);

  const tableRetryCountReference = useRef<Record<number, number>>({});

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (currentDriver?.id && baseScenario?.id && isInputDriver) {
      const driverId = Number(currentDriver.id);
      const tableIndicatorId = Number(indicatorTableData?.indicator_id);

      if (
        tableIndicatorId === driverId &&
        (indicatorTableData?.column_headers?.length ?? 0) === 0
      ) {
        const retries = tableRetryCountReference.current[driverId] ?? 0;
        if (retries < 4) {
          timer = setTimeout(() => {
            tableRetryCountReference.current[driverId] = retries + 1;
            dispatch(
              FetchPlangPageIndicatorTableData({
                indicator_id: currentDriver.id,
                parameters: {
                  scenario_id: baseScenario.id,
                  format_excel_data: true,
                },
              }),
            );
          }, 600 * (retries + 1));
        }
      } else if ((indicatorTableData?.column_headers?.length ?? 0) > 0) {
        delete tableRetryCountReference.current[driverId];
      }
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    currentDriver?.id,
    baseScenario?.id,
    indicatorTableData?.indicator_id,
    indicatorTableData?.column_headers?.length,
    isInputDriver,
    dispatch,
  ]);

  useEffect(() => {
    if (!modelId || !blockId || !currentDriver?.id || !baseScenario?.id || !isInputDriver) {
      return;
    }

    const tableMatchesDriver =
      Number(indicatorTableData?.indicator_id ?? currentDriver.id) === Number(currentDriver.id);

    const dimIdFromTable =
      tableMatchesDriver && indicatorTableData?.column_headers?.[0]?.is_dimension
        ? indicatorTableData.column_headers[0].id
        : null;
    const dimId =
      dimIdFromTable ??
      currentDriver?.data_input?.dimensions?.[currentDimensionIndex] ??
      currentDriver?.data_input?.dimensions?.[0] ??
      null;

    dispatch(
      fetchIndicatorDetail({
        currentDriver,
        parameters: {
          dim_id: dimId,
          scenario_id: baseScenario.id,
          is_chart_show: true,
        },
      }),
    );
  }, [
    currentDriver?.id,
    currentDriver?.data_input?.dimensions,
    currentDimensionIndex,
    baseScenario?.id,
    indicatorTableData?.indicator_id,
    indicatorTableData?.column_headers,
    blockId,
    modelId,
    isInputDriver,
    dispatch,
  ]);

  const [noDimChartData, setNoDimChartData] = useState<Record<string, Record<string, number>> | []>(
    [],
  );
  useEffect(() => {
    if (multiDimFiltersItems && multiDimFiltersItems.length > 0) {
      dispatch(
        fetchIndicatorDetailType({
          currentDriver: indicator,
          parameters: {
            dim_id: chartFilterDimId,
            scenario_id: baseScenario?.id,
            is_chart_show: true,
            filter_dim_items: JSON.stringify(filteredChartItems),
          },
        }),
      ).then(() => {
        dispatch(
          fetchIndicatorDetail({
            currentDriver: indicator,
            parameters: {
              dim_id: chartFilterDimId,
              scenario_id: baseScenario?.id,
              is_chart_show: true,
              filter_dim_items: JSON.stringify(filteredChartItems),
            },
          }),
        );
      });
    }
  }, [indicatorTableData?.type]);

  useEffect(() => {
    if (indicator && !indicator?.output?.dims) {
      const object: any = {};
      object[indicator.output?.name] = indicator.output?.total_values;
      setNoDimChartData(object);
    }
  }, [indicator]);

  useEffect(() => {
    if (indicatorDetailStatus === STATUS.FAILED || modelTimeStatus === STATUS.FAILED) {
      dispatch(onErrorShow(true));
    }
  }, [indicatorDetailStatus, modelTimeStatus]);

  const closeSection = () => {
    dispatch(handleToggleInputPanel());
    dispatch(handleExpandSideInput(false));
  };

  const showShimmer = indicatorDetailStatus !== STATUS.SUCCESS || tabShimmer === true;

  if (indicatorDetailStatus === STATUS.FAILED || modelTimeStatus === STATUS.FAILED) {
    return (
      <>
        {isMobile && (
          <Flex
            justifyContent={'space-between'}
            alignItems={'center'}
            p={4}
            backgroundColor={'transparent'}
          >
            <Flex onClick={closeSection} alignItems='center' cursor='pointer'>
              {isMobile && (
                <Box mr='6px' fontFamily={'regular_roboto'}>
                  <ArrowLeftBlocks />
                </Box>
              )}
              {isMobile ? (
                <Text size='assistant_md'>Inputs</Text>
              ) : (
                <Flex alignItems={'center'}>
                  <Text ml={3} size='assistant_md'>
                    {indicator?.output?.name}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Flex
              as='button'
              type='button'
              aria-label='Close input panel'
              alignItems='center'
              justifyContent='center'
              w='28px'
              h='28px'
              flexShrink={0}
              borderRadius={radius.control}
              cursor='pointer'
              transition='background 0.12s ease'
              _hover={{ bg: neutral.surfaceHover }}
              onClick={closeSection}
            >
              <Cross height='16px' width='16px' bgColor={textToken.secondary} fillOpacity={1} />
            </Flex>
          </Flex>
        )}
        <Flex alignItems={'center'} justifyContent={'center'} height={'inherit'}>
          <ErrorPage style={{ height: `calc(100vh - 83px)` }} planError={true} />
        </Flex>
      </>
    );
  }

  if (getToolbarIndicatorType(currentDriver) === 'calculation' || !currentDriver) {
    return (
      <Flex
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'center'}
        height={'inherit'}
        maxH={'100%'}
        // Same as the main panel root — Text theme inherits white from the
        // old dark chrome unless colour is anchored here.
        color={textToken.primary}
      >
        <Flex w={'100%'} justify={'space-between'} alignItems='center' pt={3} pe={2}>
          <Text ml={3} size='assistant_md'>
            {indicator?.output?.name}
          </Text>
          {/* `Cross` paints with `bgColor` (defaults to white) — invisible on
              this light panel unless we pass a dark fill. */}
          <Flex
            as='button'
            type='button'
            aria-label='Close input panel'
            alignItems='center'
            justifyContent='center'
            w='28px'
            h='28px'
            flexShrink={0}
            borderRadius={radius.control}
            cursor='pointer'
            transition='background 0.12s ease'
            _hover={{ bg: neutral.surfaceHover }}
            onClick={closeSection}
          >
            <Cross height='16px' width='16px' bgColor={textToken.secondary} fillOpacity={1} />
          </Flex>
        </Flex>
        <EmptyData />
      </Flex>
    );
  }

  const refetchDrivers = async () => {
    await dispatch(
      fetchBlockDrivers({
        blockId,
        parameters: {
          scenario_id: baseScenario?.id,
        },
      }),
    );
  };

  const refetchData = async () => {
    dispatch(
      fetchIndicatorDetail({
        currentDriver,
        parameters: {
          dim_id: chartFilterDimId,
          scenario_id: baseScenario?.id,
        },
      }),
    ).then(() => {
      dispatch(
        FetchPlangPageIndicatorTableData({
          indicator_id: currentDriver?.id,
          parameters: {
            scenario_id: baseScenario?.id,
            format_excel_data: true,
          },
        }),
      );
      // fetchModelTimeData();
      // if (currentDriver && currentDriver?.data_input?.dimensions.length > 0) {
      //   fetchDimItemsData();
      // }
    });
  };

  const onAddDimendionItem = async () => {
    await dispatch(
      addDimensionItem({
        dimension_id: currentDriver?.data_input?.dimensions?.[currentDimensionIndex],
        request_body: addNewDimensionItem(dimensionItems?.items, baseScenario?.id),
        toaster: {
          successMessage: 'Successfully added',
          errorMessage: 'An item with this name already exists',
        },
      }),
    );
    await dispatch(
      FetchDataInput({
        indicator_id: currentDriver?.id,
        parameters: {
          scenario_id: baseScenario?.id,
          format_excel_data: true,
        },
      }),
    );
    await refetchData();
  };

  return (
    /* The app's `Text` theme points `defaultProps` at a `main` size/variant
       that doesn't exist, so text carries no colour of its own and inherits
       white from the old dark chrome. Anchoring the colour on the panel root
       fixes every label at once instead of one element at a time. */
    <Flex
      display='flex'
      flexDirection='column'
      overflow='hidden'
      className='scroll'
      width='100%'
      height={'100%'}
      p={2}
      pb={'0 !important'}
      color={textToken.primary}
    >
      <Flex justifyContent={'space-between'} alignItems={'center'}>
        <Flex onClick={closeSection} alignItems='center' cursor='pointer'>
          {isMobile && (
            <Box mr='6px' fontFamily={'regular_roboto'}>
              <ArrowLeftBlocks />
            </Box>
          )}
          {isMobile ? (
            <Text size='assistant_md'>Inputs</Text>
          ) : (
            <Flex
              alignItems={'center'}
              gap={'2'}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <Text ml={3} size='assistant_md'>
                {indicator?.output?.name}
              </Text>
              {indicator?.help && (
                <Tooltip
                  isOpen={isTooltipOpen}
                  hasArrow
                  arrowSize={10}
                  bg='white'
                  placement='right'
                  boxShadow={'rgba(0, 0, 0, 0.24) 0px 3px 8px'}
                  label={
                    <VStack
                      bg={'white'}
                      h='inherit'
                      p='3'
                      justify='space-between'
                      maxWidth={'300px'}
                      width={'180px'}
                      alignItems={'center'}
                      alignContent={'center'}
                      gap={1}
                      zIndex={1}
                    >
                      <Text
                        mb='2.5'
                        fontFamily='bold_roboto'
                        fontSize='sm'
                        lineHeight='4'
                        noOfLines={2}
                        color='black'
                      >
                        Help
                      </Text>
                      <Text
                        size='modalContent'
                        fontFamily='regular_roboto'
                        noOfLines={3}
                        color='black'
                        fontSize='2xs'
                      >
                        {indicator?.help}
                      </Text>
                    </VStack>
                  }
                  borderRadius='4'
                  width={'-moz-min-content'}
                >
                  <Box
                    onClick={() => {
                      setIsTooltipOpen(!isTooltipOpen);
                    }}
                    ref={tooltipReference}
                    mt='1'
                  >
                    <QuestionMarkIcon height={18} width={18} color={textToken.secondary} />
                  </Box>
                </Tooltip>
              )}
            </Flex>
          )}
        </Flex>
        {/* `Cross` fills its glyph with `bgColor`, which defaults to white —
            fine on the panel's old purple chrome, invisible on this one. */}
        <Flex
          as='button'
          type='button'
          aria-label='Close input panel'
          alignItems='center'
          justifyContent='center'
          w='28px'
          h='28px'
          flexShrink={0}
          borderRadius={radius.control}
          cursor='pointer'
          transition='background 0.12s ease'
          _hover={{ bg: neutral.surfaceHover }}
          onClick={closeSection}
        >
          <Cross height={'16px'} width={'16px'} bgColor={textToken.secondary} fillOpacity={1} />
        </Flex>
      </Flex>

      {isMobile && (
        <Flex alignItems={'center'} mt={2}>
          <Text ml={3} size='assistant_md'>
            {indicator?.output?.name}
          </Text>
        </Flex>
      )}

      {showShimmer && (
        <Box
          h={innerHeight ? `${innerHeight}px` : '30vh'}
          pt={4}
          minH={innerHeight ? `${innerHeight}px` : '30vh'}
          mb='3'
        >
          <Skeleton
            startColor='white'
            endColor={neutral.surfaceHover}
            h={'-webkit-fill-available'}
          />
        </Box>
      )}

      {!showShimmer && (
        <Box display={{ base: 'block' }} mb='1' ref={containerReference} height={'25vh'}>
          {/* `axisTheme` defaults to 'dark', which paints the axis labels and
              gridlines white for the panel's old purple chrome — on this white
              panel that made the scale and outline disappear entirely. */}
          {indicator?.output?.dims ? (
            <Chart
              showLabel={true}
              chartLabelData={indicator?.output.total_values}
              chartData={indicator?.output?.dim_item_data}
              chartType={indicator?.output?.instrument_type}
              dimId={indicator?.output?.dims?.id}
              formatProperties={indicator?.output?.data_format_properties}
              axisTheme='light'
            />
          ) : (
            <Chart
              showLabel={true}
              chartLabelData={indicator?.output?.total_values}
              chartData={noDimChartData}
              chartType={indicator?.output?.instrument_type}
              formatProperties={indicator?.output?.data_format_properties}
              axisTheme='light'
            />
          )}
        </Box>
      )}

      <Flex
        direction='column'
        borderTopLeftRadius='base'
        borderTopRightRadius='base'
        maxHeight={isSmallScreen ? `calc(100% - 200px)` : 'auto'}
      >
        {!showPlanPage && (
          <Flex alignItems={'center'} justifyContent={'space-between'} flexShrink={0}>
            <Box width={'100%'} alignItems={'center'}>
              <InputTypeGroupedButton
                inputDimensionsData={transformDimensions(currentDriver?.data_input?.dimensions)}
                blockId={currentDriver?.data_input?.block_id ?? Number(blockId)}
                inputType={indicatorTableData?.type}
                indicatorId={indicator?.id}
                refetchDrivers={refetchDrivers}
                inputDataValues={indicatorTableData?.data_values}
                timeDimension={currentDriver?.data_input?.time_dim}
                timeRange={modelTime?.time_range}
                timeRangeYears={modelTime?.time_range_years}
                refetchRequest={refetchData}
              />
            </Box>
          </Flex>
        )}

        {showPlanPage && (
          <Box width={`${isSideInputExpand ? '30%' : '100%'}`} flexShrink={0}>
            <InputTypeGroupedButton
              inputDimensionsData={transformDimensions(currentDriver?.data_input?.dimensions)}
              blockId={currentDriver?.data_input?.block_id ?? Number(blockId)}
              inputType={indicatorTableData?.type}
              indicatorId={indicator?.id}
              refetchDrivers={refetchDrivers}
              inputDataValues={indicatorTableData?.data_values}
              timeDimension={currentDriver?.data_input?.time_dim}
              timeRange={modelTime?.time_range}
              timeRangeYears={modelTime?.time_range_years}
              refetchRequest={refetchData}
            />
          </Box>
        )}

        {isMobile && (
          <>
            <Box mt='1' mb='1' className='sm-scroll'>
              <IndicatorTable
                refetchRequest={refetchData}
                isPercentage={indicator?.data_format?.toLowerCase()}
              />
              {dimensionItems?.items && isExpand && <AddButton onClick={onAddDimendionItem} />}
            </Box>
            {indicatorTableData?.help && isExpand && (
              <Box mt={5} pb={3}>
                <HelpComponent helpText={indicatorTableData?.help} style={{ width: '100%' }} />
              </Box>
            )}
          </>
        )}

        {!isMobile && (
          <>
            <Box
              mt='1'
              mb='1'
              pb='.5rem'
              overflowY='auto'
              className='sm-scroll'
              minH={'max-content'}
            >
              <IndicatorTable
                refetchRequest={refetchData}
                isPercentage={indicator?.data_format?.toLowerCase()}
              />
              {dimensionItems?.items && isExpand && <AddButton onClick={onAddDimendionItem} />}
            </Box>
          </>
        )}
      </Flex>
    </Flex>
  );
};

export default IndicatorPageOPZ;
